import type React from "react";
import { useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  type SelectChangeEvent,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { isUserSuperUser, isUserAdmin, UserRole } from "../types/user";
import {
  importDataApi,
  type ImportDataPayload,
  type EntityType,
  type ImportDataResponse,
} from "../api/dtoolsApi";
import { toastEmitter } from "../utils/toastEmitter";
import { getErrorMessage } from "../utils/errorHandling";
import type translations from "../translations/translations.json";
import formSpec from "./resources/dataimporter-form-spec.json";

type TranslationKeys = keyof typeof translations.en;

interface FieldSpec {
  type: string;
  required?: boolean;
  default?: any;
  description?: string;
  values?: string[];
  format?: string;
  properties?: Record<string, FieldSpec>;
  items?: {
    type: string;
    required?: boolean;
    properties?: Record<string, FieldSpec>;
  };
}

interface EntityField {
  key: string;
  label: string;
  example: string | number | boolean | any[];
  required: boolean;
  description?: string;
  enumValues?: string[];
}

// Helper function to generate example value from field spec
const generateExample = (
  fieldName: string,
  fieldSpec: FieldSpec,
  path = "",
  t?: (key: string) => string,
): any => {
  const fullPath = path ? `${path}.${fieldName}` : fieldName;

  switch (fieldSpec.type) {
    case "enum":
      // Enum type - return first value as example
      return fieldSpec.values?.[0] || "";

    case "string":
      if (fieldSpec.values) {
        // Enum type stored as string with values
        return fieldSpec.values[0] || "";
      }
      if (fieldSpec.format?.includes("date") || fieldSpec.format?.includes("ISO")) {
        return "2024-01-01 00:00:00";
      }
      if (fieldName === "name") {
        return t ? t("Example Name" as TranslationKeys) : "Example Name";
      }
      if (fieldName === "_id" || fieldName.includes("Id")) {
        return t ? t("example-id-123" as TranslationKeys) : "example-id-123";
      }
      return t ? t("example-value" as TranslationKeys) : "example-value";

    case "number":
      return 1000;

    case "boolean":
      return fieldSpec.default ?? false;

    case "date":
      return "2024-01-01 00:00:00";

    case "array":
      if (fieldSpec.items?.type === "string") {
        return t
          ? [t("item1" as TranslationKeys), t("item2" as TranslationKeys)]
          : ["item1", "item2"];
      }
      if (fieldSpec.items?.type === "object" && fieldSpec.items.properties) {
        const objExample: Record<string, any> = {};
        for (const [key, spec] of Object.entries(fieldSpec.items.properties)) {
          objExample[key] = generateExample(key, spec, fullPath, t);
        }
        return [objExample];
      }
      return [];

    case "object":
      if (fieldSpec.properties) {
        const objExample: Record<string, any> = {};
        for (const [key, spec] of Object.entries(fieldSpec.properties)) {
          objExample[key] = generateExample(key, spec, fullPath, t);
        }
        return objExample;
      }
      return {};

    default:
      return "";
  }
};

// Flatten nested object fields for CSV display
const flattenFields = (
  fields: Record<string, FieldSpec>,
  path = "",
  result: EntityField[] = [],
  t?: (key: string) => string,
): EntityField[] => {
  for (const [key, spec] of Object.entries(fields)) {
    const fullPath = path ? `${path}.${key}` : key;

    if (spec.type === "object" && spec.properties) {
      flattenFields(spec.properties, fullPath, result, t);
    } else if (spec.type === "array" && spec.items?.type === "object" && spec.items.properties) {
      // For arrays of objects, show the nested properties
      for (const [nestedKey, nestedSpec] of Object.entries(spec.items.properties)) {
        result.push({
          key: `${fullPath}[].${nestedKey}`,
          label: `${fullPath}[].${nestedKey}`,
          example: generateExample(nestedKey, nestedSpec, fullPath, t),
          required: spec.required ?? false,
          description: nestedSpec.description,
          enumValues:
            nestedSpec.type === "enum" || nestedSpec.values ? nestedSpec.values : undefined,
        });
      }
    } else {
      result.push({
        key: fullPath,
        label: fullPath,
        example: generateExample(key, spec, path, t),
        required: spec.required ?? false,
        description: spec.description,
        enumValues: spec.type === "enum" || spec.values ? spec.values : undefined,
      });
    }
  }

  return result;
};

const DataImporterPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedEntity, setSelectedEntity] = useState<EntityType>("truck");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [execute, setExecute] = useState(false);
  const [validationResults, setValidationResults] = useState<ImportDataResponse | null>(null);

  const isSuperUser = isUserSuperUser(user);
  const isAdmin = isUserAdmin(user);
  const isAdminOrStaff = isAdmin || user?.role === UserRole.STAFF;
  const canAccess = isAdminOrStaff || isSuperUser;

  // Extract entity types and fields from form spec
  const entityTypes = useMemo(() => {
    return formSpec.entities.map((entity) => entity.entityName) as EntityType[];
  }, []);

  const entityFields = useMemo(() => {
    const entity = formSpec.entities.find((e) => e.entityName === selectedEntity);
    if (!entity || !entity.entries[0]) {
      return [];
    }

    const entryFields = entity.entries[0];
    return flattenFields(entryFields as Record<string, FieldSpec>, "", [], t);
  }, [selectedEntity, t]);

  // Redirect if not admin, staff, or superuser (after hooks)
  const canMutate = canAccess;

  const handleEntityChange = (event: SelectChangeEvent<EntityType>) => {
    setSelectedEntity(event.target.value as EntityType);
    setFile(null);
    setError(null);
    setValidationResults(null);
    // Reset file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
      setError(t("Please select a CSV file" as TranslationKeys));
      return;
    }
    setFile(selectedFile);
    setError(null);
    setValidationResults(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const parseCSV = async (file: File): Promise<Record<string, any>[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split("\n").filter((line) => line.trim());
          if (lines.length < 2) {
            reject(
              new Error(
                t("CSV file must have at least a header row and one data row" as TranslationKeys),
              ),
            );
            return;
          }

          const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
          const data: Record<string, any>[] = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(",").map((v) => {
              const trimmed = v.trim();
              // Remove surrounding quotes if present
              if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
                return trimmed.slice(1, -1).replace(/""/g, '"');
              }
              return trimmed;
            });

            const row: Record<string, any> = {};
            headers.forEach((header, index) => {
              let value: any = values[index] || "";

              // Handle array fields (e.g., "route[].id")
              if (header.includes("[]")) {
                const [arrayPath, nestedKey] = header.split("[].", 2);
                if (value) {
                  // Try to parse as JSON first
                  try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                      // If it's an array of objects, use as-is
                      if (parsed.length > 0 && typeof parsed[0] === "object") {
                        row[arrayPath] = parsed;
                      } else {
                        // If it's an array of primitives, convert to array of objects
                        row[arrayPath] = parsed.map((item: any) => {
                          const obj: Record<string, any> = {};
                          obj[nestedKey] = item;
                          return obj;
                        });
                      }
                    } else {
                      // Single object, wrap in array
                      row[arrayPath] = [{ [nestedKey]: parsed }];
                    }
                  } catch {
                    // If not JSON, treat as comma-separated values
                    const items = value
                      .split(",")
                      .map((item: string) => {
                        const obj: Record<string, any> = {};
                        obj[nestedKey] = item.trim();
                        return obj;
                      })
                      .filter((item: Record<string, any>) => item[nestedKey]); // Remove empty items
                    if (items.length > 0) {
                      row[arrayPath] = items;
                    }
                  }
                }
                return;
              }

              // Handle nested fields (e.g., "currentState.location.cityId")
              const keys = header.split(".");
              let current: any = row;
              for (let j = 0; j < keys.length - 1; j++) {
                if (!current[keys[j]]) {
                  current[keys[j]] = {};
                }
                current = current[keys[j]];
              }

              // Try to parse as boolean, number, or JSON
              if (value === "true" || value === "TRUE") {
                value = true;
              } else if (value === "false" || value === "FALSE") {
                value = false;
              } else if (!Number.isNaN(Number(value)) && value !== "") {
                value = Number(value);
              } else if (value.startsWith("{") || value.startsWith("[")) {
                try {
                  value = JSON.parse(value);
                } catch {
                  // Keep as string if JSON parsing fails
                }
              }

              current[keys[keys.length - 1]] = value;
            });
            data.push(row);
          }

          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error(t("Failed to read file" as TranslationKeys)));
      reader.readAsText(file);
    });
  };

  const handleImport = async () => {
    if (!file) {
      setError(t("Please select a file to import" as TranslationKeys));
      return;
    }

    setIsUploading(true);
    setError(null);
    setValidationResults(null);

    try {
      const data = await parseCSV(file);
      const payload: ImportDataPayload = {
        entity: selectedEntity,
        data,
        execute,
      };

      const result = await importDataApi(payload);
      setValidationResults(result);

      if (execute) {
        if (result.imported && result.imported > 0) {
          toastEmitter.emit(
            t("Successfully imported {{count}} {{entity}}(s)" as TranslationKeys, {
              count: result.imported,
              entity: selectedEntity,
            }),
            "success",
          );
        }
        if (result.invalid > 0) {
          toastEmitter.emit(
            t("{{invalid}} {{entity}}(s) failed validation" as TranslationKeys, {
              invalid: result.invalid,
              entity: selectedEntity,
            }),
            "warning",
          );
        }
      } else {
        if (result.valid === result.total) {
          toastEmitter.emit(t("All entities passed validation" as TranslationKeys), "success");
        } else {
          toastEmitter.emit(
            t("{{valid}} valid, {{invalid}} invalid" as TranslationKeys, {
              valid: result.valid,
              invalid: result.invalid,
            }),
            "warning",
          );
        }
      }
    } catch (err: any) {
      const errorMessage =
        getErrorMessage(err, t, "importData") ||
        err.message ||
        t("Failed to import data" as TranslationKeys);
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setIsUploading(false);
    }
  };

  const getEntityDisplayName = (entityName: EntityType): string => {
    const names: Record<EntityType, string> = {
      truck: t("Truck" as TranslationKeys),
      city: t("City" as TranslationKeys),
      vendor: t("Vendor" as TranslationKeys),
      user: t("User" as TranslationKeys),
      truckGroup: t("Truck Group" as TranslationKeys),
      truckHistory: t("Truck History" as TranslationKeys),
    };
    return names[entityName] || entityName;
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t("Data Importer" as TranslationKeys)}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t("To bulk import data, follow these steps:" as TranslationKeys)}
          </Typography>
          <Typography component="ol" sx={{ pl: 2, mb: 0 }}>
            <Typography component="li" variant="body2">
              {t("Select the entity type from the dropdown below" as TranslationKeys)}
            </Typography>
            <Typography component="li" variant="body2">
              {t(
                "Create a Microsoft Excel file with the format shown in the table below" as TranslationKeys,
              )}
            </Typography>
            <Typography component="li" variant="body2">
              {t("Save the file as CSV format (File > Save As > Format: CSV)" as TranslationKeys)}
            </Typography>
            <Typography component="li" variant="body2">
              {t("Upload the CSV file using the file upload section below" as TranslationKeys)}
            </Typography>
          </Typography>
        </Alert>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t("Select Entity" as TranslationKeys)}</InputLabel>
          <Select
            value={selectedEntity}
            onChange={handleEntityChange}
            label={t("Select Entity" as TranslationKeys)}
          >
            {entityTypes.map((entityType) => (
              <MenuItem key={entityType} value={entityType}>
                {getEntityDisplayName(entityType)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("Data Format Example" as TranslationKeys)}
          </Typography>
          <Box
            sx={{
              display: "inline-block",
              maxWidth: "100%",
              width: "fit-content",
            }}
          >
            <TableContainer
              component={Paper}
              sx={{
                border: "2px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "auto",
                maxWidth: "100%",
              }}
            >
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    border: "1px solid",
                    borderColor: "divider",
                    borderWidth: "1px",
                    padding: "8px 12px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    fontWeight: 600,
                    borderWidth: "2px",
                    borderColor: "divider",
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {entityFields.map((field) => (
                      <TableCell key={field.key} sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                        {field.key}
                        {field.required && (
                          <Typography
                            component="span"
                            sx={{
                              color: "error.main",
                              fontWeight: "bold",
                              ml: 0.5,
                            }}
                          >
                            *
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {entityFields.map((field) => (
                      <TableCell key={field.key} sx={{ fontSize: "0.875rem" }}>
                        {field.enumValues && field.enumValues.length > 0 ? (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>{t("Values:" as TranslationKeys)}</strong>{" "}
                              {field.enumValues.join(" | ")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t("Example: " as TranslationKeys)}
                              {String(field.example)}
                            </Typography>
                          </Box>
                        ) : Array.isArray(field.example) ? (
                          JSON.stringify(field.example)
                        ) : typeof field.example === "object" ? (
                          JSON.stringify(field.example)
                        ) : (
                          String(field.example)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {entityFields.length > 0 && (
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              {t("* Required fields" as TranslationKeys)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("Upload CSV File" as TranslationKeys)}
        </Typography>
        <Box
          onDragOver={canMutate ? handleDragOver : undefined}
          onDragLeave={canMutate ? handleDragLeave : undefined}
          onDrop={canMutate ? handleDrop : undefined}
          sx={{
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "grey.300",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            backgroundColor: isDragging ? "action.hover" : "background.paper",
            cursor: canMutate ? "pointer" : "not-allowed",
            opacity: canMutate ? 1 : 0.6,
            transition: "all 0.2s",
          }}
          onClick={() => {
            if (canMutate) {
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            id="file-input"
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            disabled={!canMutate}
            style={{ display: "none" }}
          />
          <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            {file
              ? file.name
              : t("Drag and drop a CSV file here, or click to select" as TranslationKeys)}
          </Typography>
          {file && canMutate && (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setValidationResults(null);
                // Reset file input value to allow selecting the same file again
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              {t("Remove" as TranslationKeys)}
            </Button>
          )}
        </Box>
      </Box>

      {canMutate && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={execute}
                onChange={(e) => setExecute(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {execute
                    ? t("Execute Import (Save to Database)" as TranslationKeys)
                    : t("Validate Only (Preview)" as TranslationKeys)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {execute
                    ? t("Valid entities will be imported to the database" as TranslationKeys)
                    : t("Entities will be validated but not saved" as TranslationKeys)}
                </Typography>
              </Box>
            }
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {validationResults && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={validationResults.invalid === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {execute
                ? t("Import Results" as TranslationKeys)
                : t("Validation Results" as TranslationKeys)}
            </Typography>
            <Typography>
              {t("Total" as TranslationKeys)}: {validationResults.total} |{" "}
              {t("Valid" as TranslationKeys)}: {validationResults.valid} |{" "}
              {t("Invalid" as TranslationKeys)}: {validationResults.invalid}
              {execute && validationResults.imported !== undefined && (
                <>
                  {" "}
                  | {t("Imported" as TranslationKeys)}: {validationResults.imported}
                </>
              )}
            </Typography>
          </Alert>

          {validationResults.validations && validationResults.validations.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  {t("View Validation Details" as TranslationKeys)} (
                  {validationResults.validations.length} {t("items" as TranslationKeys)})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("Status" as TranslationKeys)}</TableCell>
                        <TableCell>{t("Entity Data" as TranslationKeys)}</TableCell>
                        <TableCell>{t("Reason" as TranslationKeys)}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationResults.validations.map((validation, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {validation.validation.isValid ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label={t("Valid" as TranslationKeys)}
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<ErrorIcon />}
                                label={t("Invalid" as TranslationKeys)}
                                color="error"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                              }}
                            >
                              {JSON.stringify(validation.entity, null, 2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {validation.validation.invalidReason ? (
                              <Typography variant="body2" color="error">
                                {validation.validation.invalidReason}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="success.main">
                                {t("Valid" as TranslationKeys)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {canMutate && (
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!file || isUploading}
          color={execute ? "warning" : "primary"}
          startIcon={isUploading ? <CircularProgress size={20} /> : undefined}
        >
          {isUploading
            ? execute
              ? t("Importing..." as TranslationKeys)
              : t("Validating..." as TranslationKeys)
            : execute
              ? t("Import Data" as TranslationKeys)
              : t("Validate Data" as TranslationKeys)}
        </Button>
      )}
    </Box>
  );
};

export default DataImporterPage;
